/**
 * BitTorrent control adapter for zapd's libtorrent daemon.
 *
 * The UI still addresses torrents by their BTv1 hash while the canonical
 * zapd HTTP API uses SQLite torrent ids. This module owns that translation
 * and keeps the view layer independent from the daemon's wire format.
 */

import { api } from './api.js';

const HASH_RE = /^[A-Fa-f0-9]{40}$/;
const BASE = '/api-zapd/v1/torrent';
const idCache = new Map();

function ensureHash(hash) {
  if (!HASH_RE.test(String(hash || ''))) {
    throw new Error(`bt: invalid hash "${hash}"`);
  }
  return String(hash).toLowerCase();
}

async function torrentList() {
  const result = await api(BASE);
  const rows = Array.isArray(result?.torrents) ? result.torrents : [];
  for (const row of rows) {
    if (row.info_hash && row.id) idCache.set(String(row.info_hash).toLowerCase(), Number(row.id));
  }
  return rows;
}

async function resolveId(hash) {
  const normalized = ensureHash(hash);
  const cached = idCache.get(normalized);
  if (cached) return cached;
  const row = (await torrentList()).find((t) => String(t.info_hash).toLowerCase() === normalized);
  if (!row) throw new Error('Torrent nicht gefunden');
  return Number(row.id);
}

async function getTorrent(hash) {
  const id = await resolveId(hash);
  const result = await api(`${BASE}/${id}`);
  if (!result?.torrent) throw new Error('Torrent-Details fehlen');
  return result.torrent;
}

function humanBytes(bytes) {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = Number(bytes || 0);
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value.toFixed(value < 10 && unit > 0 ? 1 : 0)} ${units[unit]}`;
}

function fileView(file) {
  const size = Number(file.size || 0);
  const done = Number(file.done || 0);
  const pct = size > 0 ? Math.min(100, Math.round((done / size) * 1000) / 10) : 0;
  const path = String(file.path || '');
  const dot = path.lastIndexOf('.');
  return {
    ...file,
    name: path,
    size,
    done,
    pct,
    sizeHuman: humanBytes(size),
    ext: dot >= 0 ? path.slice(dot + 1).toLowerCase() : '',
    complete: size > 0 && done >= size,
    streamUrl: null,
  };
}

export async function btList(opts = {}) {
  let rows = await torrentList();
  if (opts.label) rows = rows.filter((row) => row.label === opts.label);
  if (opts.q) {
    const q = String(opts.q).toLowerCase();
    rows = rows.filter((row) =>
      String(row.name || '')
        .toLowerCase()
        .includes(q),
    );
  }
  if (opts.view === 'completed') rows = rows.filter((row) => Number(row.percentage) >= 100);
  if (opts.view === 'incomplete') rows = rows.filter((row) => Number(row.percentage) < 100);
  if (opts.view === 'zap') rows = rows.filter((row) => row.remove_state === 'remove');
  const offset = Math.max(0, Number(opts.offset || 0));
  const limit = Math.max(1, Number(opts.limit || rows.length || 100));
  return { torrents: rows.slice(offset, offset + limit), total: rows.length };
}

export async function btDetail(hash) {
  const t = await getTorrent(hash);
  const rt = t.runtime || {};
  const downloaded = Number(rt.total_download ?? rt.total_done ?? 0);
  const uploaded = Number(rt.total_upload || 0);
  const progress = Number(rt.progress || 0);
  return {
    detail: {
      name: t.name,
      is_active: !rt.is_paused,
      complete: progress >= 1 || t.state === 'seeding',
      size_bytes: Number(t.total_size || 0),
      completed_bytes: Number(rt.total_done || 0),
      ratio: downloaded > 0 ? Math.round((uploaded / downloaded) * 1000) : 0,
      up_total: uploaded,
      down_total: downloaded,
      up_rate: Number(rt.upload_rate || 0),
      down_rate: Number(rt.download_rate || 0),
      peers_connected: Number(rt.num_peers || 0),
      peers_complete: Number(rt.num_seeds || 0),
      directory: t.save_path,
      creation_date: Number(t.added_at || 0),
      size_files: Array.isArray(t.files) ? t.files.length : 0,
      chunk_size: Number(t.piece_length || 0),
      chunks_hashed: Math.round(progress * Number(t.num_pieces || 0)),
      chunks_total: Number(t.num_pieces || 0),
      custom1: t.label || '',
      throttle_name: '',
    },
  };
}

export async function btFiles(hash) {
  const t = await getTorrent(hash);
  return { files: (t.files || []).map(fileView) };
}

export async function btPeers(hash) {
  const t = await getTorrent(hash);
  return { peers: t.peers || [] };
}

export async function btTrackers(hash) {
  const t = await getTorrent(hash);
  return { trackers: t.trackers || [] };
}

export async function btWebSeeds(hash) {
  const t = await getTorrent(hash);
  return { webseeds: t.webseeds || [] };
}

async function postAction(hash, action) {
  const id = await resolveId(hash);
  return api(`${BASE}/${id}/${action}`, { method: 'POST', body: {} });
}

export const btStart = (hash) => postAction(hash, 'resume');
export const btStop = (hash) => postAction(hash, 'stop');
export const btPause = (hash) => postAction(hash, 'pause');
export const btResume = (hash) => postAction(hash, 'resume');
export const btRecheck = (hash) => postAction(hash, 'recheck');

export async function btErase(hash) {
  const normalized = ensureHash(hash);
  const id = await resolveId(normalized);
  const result = await api(`${BASE}/${id}`, { method: 'DELETE' });
  idCache.delete(normalized);
  return result;
}

export async function btEraseData(hash) {
  const normalized = ensureHash(hash);
  const id = await resolveId(normalized);
  const result = await api(`${BASE}/${id}?delete_files=1`, { method: 'DELETE' });
  idCache.delete(normalized);
  return result;
}

export async function btSetLabel(hash, label) {
  const id = await resolveId(hash);
  return api(`${BASE}/${id}/label`, { method: 'PUT', body: { label: String(label ?? '') } });
}

export async function btSetPriority(hash, fileIndex, priority) {
  if (![0, 1, 2].includes(priority)) throw new Error('bt: priority must be 0|1|2');
  const id = await resolveId(hash);
  return api(`${BASE}/${id}/files/${Number(fileIndex)}/priority`, {
    method: 'PUT',
    body: { priority },
  });
}

export async function btSequential(hash, mode = 'off') {
  if (!['off', 'head', 'streaming'].includes(mode)) {
    throw new Error('bt: mode must be off|head|streaming');
  }
  const id = await resolveId(hash);
  return api(`${BASE}/${id}/sequential`, { method: 'PUT', body: { mode } });
}

export async function btTrackerAdd(hash, url, group = 0) {
  const id = await resolveId(hash);
  return api(`${BASE}/${id}/trackers`, {
    method: 'POST',
    body: { url: String(url), tier: Number(group) },
  });
}

export async function btTrackerRemove(hash, index) {
  const id = await resolveId(hash);
  return api(`${BASE}/${id}/trackers/${Number(index)}`, { method: 'DELETE' });
}

async function setTrackerEnabled(hash, index, enabled) {
  const id = await resolveId(hash);
  return api(`${BASE}/${id}/trackers/${Number(index)}`, {
    method: 'PUT',
    body: { enabled },
  });
}

export const btTrackerDisable = (hash, index) => setTrackerEnabled(hash, index, false);
export const btTrackerEnable = (hash, index) => setTrackerEnabled(hash, index, true);
export const btTrackerAnnounce = (hash) => postAction(hash, 'announce');

export async function btMarkRemove(hash) {
  return postAction(hash, 'mark-remove');
}

export async function btProcess(hash) {
  const id = await resolveId(hash);
  return api(`/api-zapd/torrent/${id}/process`, { method: 'POST', body: {} });
}
